/** Facts as a bordered grid: a shaded label cell beside its value cell, two pairs to a row, under
 * tinted section header bars - the record page's Details tab and every "key facts" block that wants
 * to read like a form rather than a list (ADR-0212).
 *
 * It is a definition list (`dl`) laid out on four columns, so a screen reader meets term and
 * definition pairs in order and a sighted reader scans labels down the shaded cells. A field marked
 * `wide` takes the whole row (a long reference, a hash, a note). **A value is whatever the caller
 * hands over** - a chip, a link, text - and nothing here computes it; a missing value is the caller's
 * to say in words ("Not set"), never an empty cell. An odd field leaves its row's second pair as a
 * blank bordered cell so the grid keeps its edges.
 */

import type { ReactNode } from "react";

import styles from "./FieldGrid.module.css";

export interface Field {
  label: string;
  value: ReactNode;
  /** Take the whole row. */
  wide?: boolean;
  /** Render the value in the monospace face: hashes, ids, citations. */
  mono?: boolean;
  testId?: string;
}

export interface FieldSection {
  /** The tinted bar above the section's cells. Optional for a single unnamed block. */
  title?: string;
  fields: readonly Field[];
}

/** Rows as lists of fields: a wide field is a row by itself, other fields pair up two to a row. */
export function rowsOf(fields: readonly Field[]): readonly (readonly Field[])[] {
  const rows: Field[][] = [];
  let open: Field[] | undefined;
  for (const field of fields) {
    if (field.wide) {
      open = undefined;
      rows.push([field]);
    } else if (open) {
      open.push(field);
      open = open.length === 2 ? undefined : open;
    } else {
      open = [field];
      rows.push(open);
    }
  }
  return rows;
}

function Cells({ field }: { field: Field }) {
  return (
    <>
      <dt className={styles.label}>{field.label}</dt>
      <dd
        className={`${styles.value} ${field.mono ? styles.mono : ""} ${field.wide ? styles.wide : ""}`}
        data-testid={field.testId}
      >
        {field.value}
      </dd>
    </>
  );
}

export function FieldGrid({
  sections,
  as: Heading = "h3",
  testId,
}: {
  sections: readonly FieldSection[];
  /** The level of the section bars. A page's `h1` is the header's, and cards under it are `h2`, so
   * the bars default to `h3`. */
  as?: "h2" | "h3" | "h4";
  testId?: string;
}) {
  return (
    <div className={styles.grid} data-testid={testId}>
      {sections.map((section, at) => (
        <section key={section.title ?? at} className={styles.section}>
          {section.title ? <Heading className={styles.bar}>{section.title}</Heading> : null}
          <dl className={styles.cells}>
            {rowsOf(section.fields).map((row) => (
              <div key={row[0]?.label} className={styles.row}>
                {row.map((field) => (
                  <Cells key={field.label} field={field} />
                ))}
                {row.length === 1 && !row[0]?.wide ? (
                  <>
                    <dd className={styles.blank} aria-hidden="true" />
                    <dd className={styles.blank} aria-hidden="true" />
                  </>
                ) : null}
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}
